// src/admin/setTextContent.js
//
// Set Text Analysis -- the canonical field mapping between a set_text item's stored
// content (functions/src/schema/packSchema.js validateItemContent 'set_text') and the
// editor's React form state. Pure + dependency-free (no React/firebase) so the
// functions test runner can import it.
//
// FIDELITY IS CRITICAL (Danielle's data): the editor's state keys ARE the SCHEMA keys.
// Prototype aliases are accepted ONLY at the load boundary and normalised IN:
//   visualMindMap -> mindMap   essays -> modelEssays   audios -> audioRefs
// buildSetTextContent emits SCHEMA names only. Save sends ALL schema-known fields
// (incl. fullText, audioRefs, annotations) so a wholesale-replace Save never erases
// data that isn't visibly edited.
//
// Schema field names (packSchema.js L154-166): title, author, year, fullText, coreIdea,
// mindMap{root,branches:[{title,leaves[]}]}, outline[str], themes[{theme,quote,analysis,
// connection,error}], modelEssays{level->text}, annotations[], audioRefs[{title,src}].

function str(v) { return typeof v === 'string' ? v : ''; }
function strArray(v) { return Array.isArray(v) ? v.map(str) : []; }

// Coerce a year text input to the schema's number|null. Empty -> null; all-digits ->
// number; anything else -> error (caller blocks save). NEVER returns a string year.
export function coerceYear(raw) {
    const s = String(raw ?? '').trim();
    if (s === '') return { year: null, error: null };
    if (/^\d+$/.test(s)) return { year: Number(s), error: null };
    return { year: null, error: 'Year must be a whole number (e.g. 2014) or left blank.' };
}

function normMindMapForEdit(v) {
    const mm = (v && typeof v === 'object') ? v : {};
    const branches = Array.isArray(mm.branches)
        ? mm.branches.filter((b) => b && typeof b === 'object').map((b) => ({ title: str(b.title), leaves: strArray(b.leaves) }))
        : [];
    return { root: str(mm.root), branches };
}

function normThemesForEdit(v) {
    if (!Array.isArray(v)) return [];
    return v.filter((t) => t && typeof t === 'object').map((t) => ({
        theme: str(t.theme), quote: str(t.quote), analysis: str(t.analysis),
        connection: str(t.connection), error: str(t.error)
    }));
}

function normEssaysForEdit(v) {
    if (!v || typeof v !== 'object' || Array.isArray(v)) return {};
    const out = {};
    for (const k of Object.keys(v)) out[k] = str(v[k]);
    return out;
}

function normAudioForEdit(v) {
    if (!Array.isArray(v)) return [];
    return v.filter((a) => a && typeof a === 'object').map((a) => ({ title: str(a.title), src: str(a.src) }));
}

// Stored content (or legacy/imported raw) -> canonical, schema-shaped form state.
// Accepts the prototype aliases visualMindMap / essays / audios at THIS boundary only.
export function normalizeSetTextContent(raw) {
    const c = raw || {};
    return {
        title: str(c.title),
        author: str(c.author),
        year: Number.isInteger(c.year) ? c.year : null, // stored year is already number|null
        fullText: str(c.fullText),
        coreIdea: str(c.coreIdea),
        mindMap: normMindMapForEdit(c.mindMap || c.visualMindMap),
        outline: strArray(c.outline),
        themes: normThemesForEdit(c.themes),
        modelEssays: normEssaysForEdit(c.modelEssays || c.essays),
        audioRefs: normAudioForEdit(c.audioRefs || c.audios),
        annotations: Array.isArray(c.annotations) ? c.annotations : [] // passthrough -- preserved, no UI
    };
}

// Canonical form state -> schema-shaped content for adminItemCreate/Update.
// Emits SCHEMA names only (mindMap/modelEssays/audioRefs -- never the aliases) and
// includes EVERY schema-known field so a wholesale-replace Save preserves all data.
// `year` is expected already coerced to number|null (see coerceYear); a stray
// non-integer is forced to null defensively.
export function buildSetTextContent(form) {
    const f = form || {};
    return {
        title: str(f.title).trim(),
        author: str(f.author),
        year: Number.isInteger(f.year) ? f.year : null,
        fullText: str(f.fullText),
        coreIdea: str(f.coreIdea),
        mindMap: { root: str(f.mindMap && f.mindMap.root), branches: (f.mindMap && Array.isArray(f.mindMap.branches)) ? f.mindMap.branches : [] },
        outline: strArray(f.outline),
        themes: normThemesForEdit(f.themes),
        modelEssays: normEssaysForEdit(f.modelEssays),
        audioRefs: normAudioForEdit(f.audioRefs),
        annotations: Array.isArray(f.annotations) ? f.annotations : [] // preserve (schema-known)
    };
}

// Mirrors the backend hasContent guard (packSchema.js L167-172) so the UI can warn
// BEFORE calling the CF. true if the item has at least one piece of real content.
export function hasSetTextContent(form) {
    const f = form || {};
    const mm = f.mindMap || {};
    const mmHas = str(mm.root).trim() || (Array.isArray(mm.branches) && mm.branches.length > 0);
    const essaysHas = f.modelEssays && Object.values(f.modelEssays).some((t) => str(t).trim());
    return !!(
        str(f.fullText).trim() || str(f.coreIdea).trim()
        || (Array.isArray(f.outline) && f.outline.some((o) => str(o).trim()))
        || (Array.isArray(f.themes) && f.themes.some((t) => t && (str(t.theme).trim() || str(t.quote).trim() || str(t.analysis).trim())))
        || essaysHas || mmHas
    );
}
